import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'
import { ApiError } from '../../api/problem'
import { useAuth } from '../../auth/AuthProvider'
import { Banner, Button, Input } from '../../design/components'
import { AuthLayout } from './AuthLayout'
import styles from './AuthForm.module.css'

interface LocationState {
  from?: string
}

export function LoginPage() {
  const { status, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const from = (location.state as LocationState | null)?.from ?? '/characters'

  if (status === 'authenticated') {
    return <Navigate to={from} replace />
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)

    try {
      await signIn(email, password)
      void navigate(from, { replace: true })
    } catch (cause) {
      setError(messageFor(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          No account yet? <Link to="/register">Create one</Link>
        </>
      }
    >
      <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
        {error !== null && (
          <Banner tone="error" urgent>
            {error}
          </Banner>
        )}

        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Button type="submit" fullWidth disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  )
}

/**
 * The API answers a bad password, an unconfirmed email and a locked-out account
 * all with 401, but with different ProblemDetails titles — and the difference
 * matters, because two of the three tell the user what to actually do about it.
 */
function messageFor(cause: unknown): string {
  if (!(cause instanceof ApiError)) {
    return 'Something went wrong. Please try again.'
  }

  if (cause.kind === 'network') {
    return 'Could not reach the server. Check that the API is running.'
  }

  const title = cause.title ?? ''

  if (title.includes('not confirmed')) {
    return 'Your email address is not confirmed yet. Use the link in your confirmation email first.'
  }

  if (title.includes('locked out')) {
    return 'This account is temporarily locked after too many failed attempts. Try again shortly.'
  }

  if (cause.kind === 'unauthorized') {
    return 'That email and password do not match an account.'
  }

  return cause.message
}
